import { TranslationKey } from './translation/TranslationKey';
import { cloneDeep } from '@apollo/client/utilities';
import { VentoryColor } from '../VentoryUI/util/color.util';
import { forCreate, forDelete, forUpdate, StockLocationEntity } from './common/entity';
import { Company } from './company';
import { t } from './translation/Translator';
import { EntityType } from './comment';
import { DateTimeUtil } from '../util/date.util';

export enum TaskStatus {
  created = 'created',
  inProgress = 'inProgress',
  complete = 'complete',
  processed = 'processed',
  failed = 'failed',
  overdue = 'overdue',
}

export enum TaskType {
  cycle = 'cycle',
  blind = 'blind',
  recount = 'recount',
}

export function validateTaskType(company: Company, type: TaskType): boolean {
  switch (type) {
    case TaskType.cycle:
      return company.settings.featureToggles.tasks.cycleCount;
    case TaskType.blind:
      return company.settings.featureToggles.tasks.blindCount;
    case TaskType.recount:
      return false;
  }
}

export function taskTypeToString(type: TaskType) {
  switch (type) {
    case TaskType.cycle:
      return 'Cycle Count';
    case TaskType.blind:
      return 'Blind Count';
    case TaskType.recount:
      return 'Recount';
  }
}

export enum TaskParentType {
  task = 'task',
}

export enum TaskValidationField {
  serial = 'serial',
  productNumber = 'productNumber',
  lpn = 'lpn',
  coo = 'coo',
  lot = 'lot',
  container = 'container',
}

export class TaskValidation {
  quantity: boolean = false;
  validationFields: TaskValidationField[] = [];
}

export class TaskIdentifier {
  ids: string[];
  type: EntityType;

  constructor(ids: string[], type: EntityType) {
    this.ids = ids;
    this.type = type;
  }
}

export class Task extends StockLocationEntity {
  assignedTo: string[] = [];
  assignedForRecount: string[] = [];
  status!: TaskStatus;
  type: TaskType = TaskType.cycle;
  identifiers!: TaskIdentifier[];
  parentId?: string = undefined;
  parentType?: TaskParentType = undefined;
  externalReferenceId?: string;
  dueDate?: string;
  completedOn?: string;
  additionalEmails: string[] = [];
  validation: TaskValidation = new TaskValidation();
  overdue: boolean = false;
  number!: string;

  binIds() {
    return this.identifiers.filter(item => item.type === EntityType.bin).flatMap(item => item.ids);
  }

  productMasterDataIds() {
    return this.identifiers.filter(item => item.type === EntityType.pmd).flatMap(item => item.ids);
  }

  containerIds() {
    return this.identifiers.filter(item => item.type === EntityType.container).flatMap(item => item.ids);
  }

  lotIds() {
    return this.identifiers.filter(item => item.type === EntityType.lot).flatMap(item => item.ids);
  }

  serials() {
    return this.identifiers.filter(item => item.type === EntityType.serial).flatMap(item => item.ids);
  }

  kits() {
    return this.identifiers.filter(item => item.type === EntityType.kit).flatMap(item => item.ids);
  }

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId, obj.stockLocationId);
    Object.assign(this, cloneDeep(obj));
    this.dueDate = DateTimeUtil.toPureDate(obj.dueDate);
    if (obj.overdue) this.status = TaskStatus.overdue;
    if (obj.parentId) this.type = TaskType.recount;
  }

  forUpdate(): UpdateTaskInput {
    return UpdateTaskInput.from(this, UpdateTaskInput);
  }

  forDelete(): DeleteTaskInput {
    this.validate();
    return DeleteTaskInput.from(this, DeleteTaskInput);
  }

  validate() {
    return this.validateEntity(Object.getOwnPropertyNames(this) as (keyof Task)[], (field: keyof Task) => {
      return null;
    });
  }

  withType(type: TaskType) {
    this.type = type;
    return cloneDeep(this);
  }

  withExternalReference(reference: string) {
    this.externalReferenceId = reference;
    return this;
  }

  withStockLocationId(stockLocationId?: string) {
    this.identifiers = [];
    (this.stockLocationId as any) = stockLocationId;
    return cloneDeep(this);
  }

  withProductMasterDataIds(ids: string[]) {
    this.identifiers = [new TaskIdentifier(ids, EntityType.pmd)];
    return cloneDeep(this);
  }

  withBinIds(ids: string[]) {
    this.identifiers = [new TaskIdentifier(ids, EntityType.bin)];
    return cloneDeep(this);
  }

  withContainerIds(ids: string[]) {
    this.identifiers = [new TaskIdentifier(ids, EntityType.container)];
    return cloneDeep(this);
  }

  withLotIds(ids: string[]) {
    this.identifiers = [new TaskIdentifier(ids, EntityType.lot)];
    return cloneDeep(this);
  }

  withSerials(serials: string[]) {
    this.identifiers = [new TaskIdentifier(serials, EntityType.serial)];
    return cloneDeep(this);
  }

  withKits(kits: string[]) {
    this.identifiers = [new TaskIdentifier(kits, EntityType.kit)];
    return cloneDeep(this);
  }

  withQuantityValidation(validation: boolean) {
    this.validation.quantity = validation;
    return cloneDeep(this);
  }

  resetValidation() {
    this.validation.validationFields = [];
    return cloneDeep(this);
  }

  withLPNValidation(field: boolean) {
    const index = this.validation.validationFields.findIndex(v => v === TaskValidationField.lpn);
    if (index > -1) this.validation.validationFields.splice(index, 1);

    if (field) this.validation.validationFields.push(TaskValidationField.lpn);
    return cloneDeep(this);
  }

  hasLPNValidation() {
    return this.validation.validationFields.includes(TaskValidationField.lpn);
  }

  withSerialValidation(field: boolean) {
    const index = this.validation.validationFields.findIndex(v => v === TaskValidationField.serial);
    if (index > -1) this.validation.validationFields.splice(index, 1);

    if (field) this.validation.validationFields.push(TaskValidationField.serial);
    return cloneDeep(this);
  }

  hasSerialValidation() {
    return this.validation.validationFields.includes(TaskValidationField.serial);
  }

  withProductNumberValidation(field: boolean) {
    const index = this.validation.validationFields.findIndex(v => v === TaskValidationField.productNumber);
    if (index > -1) this.validation.validationFields.splice(index, 1);

    if (field) this.validation.validationFields.push(TaskValidationField.productNumber);
    return cloneDeep(this);
  }

  hasProductNumberValidation() {
    return this.validation.validationFields.includes(TaskValidationField.productNumber);
  }

  withCOOValidation(field: boolean) {
    const index = this.validation.validationFields.findIndex(v => v === TaskValidationField.coo);
    if (index > -1) this.validation.validationFields.splice(index, 1);

    if (field) this.validation.validationFields.push(TaskValidationField.coo);
    return cloneDeep(this);
  }

  hasCOOValidation() {
    return this.validation.validationFields.includes(TaskValidationField.coo);
  }

  withLotValidation(field: boolean) {
    const index = this.validation.validationFields.findIndex(v => v === TaskValidationField.lot);
    if (index > -1) this.validation.validationFields.splice(index, 1);

    if (field) this.validation.validationFields.push(TaskValidationField.lot);
    return cloneDeep(this);
  }

  hasLotValidation() {
    return this.validation.validationFields.includes(TaskValidationField.lot);
  }

  withAssignedTo(ids: string[]) {
    this.assignedTo = ids;
    return cloneDeep(this);
  }

  withAssignedForRecount(ids: string[]) {
    this.assignedForRecount = ids;
    return cloneDeep(this);
  }

  withDueDate(date?: string) {
    this.dueDate = date;
    return cloneDeep(this);
  }

  withAdditionalEmails(additionalEmails: string[]) {
    this.additionalEmails = additionalEmails;
    return cloneDeep(this);
  }
}

export class CreateTaskInput extends forCreate(Task) {}

export class UpdateTaskInput extends forUpdate(Task) {}

export class DeleteTaskInput extends forDelete(Task) {}

export function taskStatusToString(status: string): string {
  switch (status) {
    case TaskStatus.created:
      return t(TranslationKey.created).singular.label;
    case TaskStatus.inProgress:
      return t(TranslationKey.inProgress).singular.label;
    case TaskStatus.complete:
      return t(TranslationKey.completed).singular.label;
    case TaskStatus.processed:
      return t(TranslationKey.processed).singular.label;
    case TaskStatus.failed:
      return t(TranslationKey.failed).singular.label;
    case TaskStatus.overdue:
      return t(TranslationKey.overdue).singular.label;
  }

  return status;
}

export function taskStatusToColorClass(status: TaskStatus) {
  switch (status) {
    case TaskStatus.created:
    case TaskStatus.processed:
      return {
        color: VentoryColor.blue700,
        backgroundColor: VentoryColor.blue50,
      };
    case TaskStatus.complete:
      return {
        color: VentoryColor.green700,
        backgroundColor: VentoryColor.green50,
      };
    case TaskStatus.inProgress:
      return {
        color: VentoryColor.yellow700,
        backgroundColor: VentoryColor.yellow50,
      };
    case TaskStatus.failed:
    case TaskStatus.overdue:
      return {
        color: VentoryColor.red700,
        backgroundColor: VentoryColor.red50,
      };
  }
}
